import React from 'react'
import { pageCategoryPageV3, ReviewsResponse } from '../types/Contentful/ContentfulTypes'
import { BFFPageData } from '../services/serviceClients/BffClient'
import {
  CategoryPageSection,
  CategoryPageSectionType,
  InformationLaneType,
  InformationMediaType,
  PageHeaderType,
  ProductGridType,
  ProductLaneType,
  SideBySideHeroType,
  StockOptionsType,
  TemplateCollectionType,
  TemplateConfiguration,
  BestsellersType,
  NewArrivalsType
} from '../types/TemplateConfiguration'
import { FeatureFlags, Translated } from '../types/Global'

import { ProductGrid } from './ProductGrid'
import { ProductLane } from './ProductLane'
import GoodBetterBest from './GoodBetterBest'
import PromoBanner from './PromoBanner'
import Seo, { showSeoSection } from './Seo'
import SocialCurations, { showOlapic } from './SocialCurations'
import VirtualSection from './VirtualSection'
import TemplateLane, { showTemplateLane } from './TemplateLane'
import PageSectionWrapper from './PageSectionWrapper'
import { useAppContext } from '../state/AppContext'
import PageHeaderContainer from './header/PageHeaderContainer'
import { useExperimentsContext } from './ExperimentsProvider'
import { addNewTestSections, ExperimentData, TestSection } from '../ab-tests/utils/TestSection'
import {
  getMarginBottom,
  isAnchorLinksEnabled,
  updateSectionCount,
  updateSectionIdforAnchorLinks
} from '../utils/pageSectionUtil/pageSectionUtil'
import { isOfTypePageHeader } from '../utils/standardHeroContents/standardHeroContents'
import { InformationLane } from './InformationLane'
import InformationMedia, { showInformationMediaSection } from './InformationMedia'
import SideBySideHero, { showSideBySideHero } from './SideBySideHero'
import { GoodBetterBestSection } from '../types/GoodBetterBest'
import { VirtualSection as VirtualSectionType } from '../types/VirtualSection'
import { TemplateLaneType } from '../types/TemplateLane'
import AnchorBarWrapper from './AnchorBarWrapper'
import CategoryRowWrapper from './CategoryRowWrapper'
import { BoundedContent } from '@vp/swan'
import AutomatedStockOptions from './AutomatedStockOptions/StockOptions'
import ProductTileCollectionUtil from '../utils/productTileCollection/productTileCollection'
import { AutomatedExperience } from './AutoGenerate/types/AutomatedExperience'
import DesignTemplate from './DesignTemplate'
import { buildStockOptionModel } from './AutomatedStockOptions/StockOptionUtil'
import { Recommendation } from './Recommendation'
import CategoryGridWrapper from './CategoryGridWrapper'
import Bestsellers from './Bestsellers/Bestsellers'
import NewArrivals from './NewArrivals/NewArrivals'
import {
  PageLevel,
  ProductRecommendationsData,
  ProductRecommendationsResponse
} from '../types/Recommendations'
import { type ExtendedMtsLocalizationResponse } from '@vp/ep-tiles'
import { type AdaptedResponse } from '@vp/product-recommendations-tracking'
import { adaptMcsResponse } from '../utils/recommendations/productRecommendationsBuilder'

const hasTitle = (
  section: CategoryPageSection
): section is CategoryPageSection & { title: Translated } => {
  return 'title' in section && section.title !== undefined
}

const getSectionTitle = (section: CategoryPageSection | undefined): Translated | undefined => {
  if (!section) return undefined
  if (hasTitle(section)) {
    return section.title
  }
  return undefined
}

interface PageSectionsProps {
  sections: CategoryPageSection[]
  pageCategoryPageV3: pageCategoryPageV3
  pageDataFromBFF: BFFPageData
  templateConfiguration: TemplateConfiguration
  automatedExperience?: AutomatedExperience
  featureFlags: FeatureFlags | undefined
  productRecommendations?: ProductRecommendationsData
  rawProductRecommendations?: ProductRecommendationsResponse
  pageLevel?: PageLevel | null
}

const PageSections = (props: PageSectionsProps) => {
  const {
    sections,
    pageCategoryPageV3,
    pageDataFromBFF,
    templateConfiguration,
    automatedExperience,
    featureFlags,
    productRecommendations,
    rawProductRecommendations,
    pageLevel
  } = props
  const {
    locale,
    merchandisingCategoryId,
    experiments,
    reviewsData,
    enableCampaignCallouts,
    sidebarEnabled,
    calloutsLocalization
  } = useAppContext()
  const experimentData = useExperimentsContext()
  const sectionsWithCount = React.useMemo(() => {
    return updateSectionCount(sections)
  }, [sections])

  const sectionsWithAnchorId = React.useMemo(() => {
    if (isAnchorLinksEnabled(sectionsWithCount)) {
      return updateSectionIdforAnchorLinks(sectionsWithCount)
    }
    return sectionsWithCount
  }, [sections])

  const adaptedNewArrivalsResponse = React.useMemo(
    () => adaptMcsResponse(rawProductRecommendations?.newArrivals),
    [rawProductRecommendations?.newArrivals]
  )

  const adaptedBestsellersResponse = React.useMemo(
    () => adaptMcsResponse(rawProductRecommendations?.bestsellers),
    [rawProductRecommendations?.bestsellers]
  )

  const sectionComponents = React.useMemo(() => {
    return sectionsWithAnchorId.map((section, index, sections) => {
      const sectionAvailable = !isOfTypePageHeader(section.type)
      if (!sectionAvailable) {
        return null
      }

      const filteredSections = sections.filter((s) => section.type === s.type)
      const number = filteredSections.findIndex((s) => s === sections[index]) + 1
      const sectionId = section.sectionId

      const nextSectionValue = index < sections.length - 1 ? sections[index + 1] : undefined
      const sectionComponent = getSectionComponent(
        {
          sections,
          section,
          pageDataFromBFF,
          merchandisingCategoryId,
          type: section.type,
          key: index,
          pageCategoryPageV3,
          locale,
          sectionNumber: number,
          ...(nextSectionValue !== undefined && { nextSection: nextSectionValue }),
          ...(reviewsData !== undefined && { reviewsData }),
          ...(enableCampaignCallouts !== undefined && { enableCampaignCallouts }),
          index,
          sidebarEnabled,
          calloutsLocalization
        },
        templateConfiguration,
        experimentData,
        automatedExperience,
        featureFlags,
        productRecommendations,
        adaptedNewArrivalsResponse,
        adaptedBestsellersResponse,
        pageLevel
      )

      const currentSection = sectionsWithAnchorId[index]
      const nextSection = sectionsWithAnchorId[index + 1]

      const marginBottom = getMarginBottom(currentSection, nextSection)

      return (
        sectionComponent && (
          <PageSectionWrapper
            type={section.type}
            marginBottom={marginBottom}
            key={`section-${section.type}-${index}`}
          >
            {section.type === ('anchor links' as CategoryPageSectionType) ? (
              <>{sectionComponent}</>
            ) : (
              <div id={sectionId} tabIndex={-1}>
                {sectionComponent}
              </div>
            )}
          </PageSectionWrapper>
        )
      )
    })
  }, [
    experimentData,
    automatedExperience,
    featureFlags,
    productRecommendations,
    adaptedNewArrivalsResponse,
    adaptedBestsellersResponse,
    pageLevel,
    sidebarEnabled
  ])

  const sectionComponentsWithTests = React.useMemo(() => {
    return addNewTestSections({
      experimentData,
      sections: sectionComponents,
      pageCategoryPageV3,
      pageDataFromBFF,
      locale,
      experiments,
      merchandisingCategoryId
    })
  }, [experimentData])

  return <>{sectionComponentsWithTests}</>
}

const getSectionComponent = (
  sectionProps: {
    sections: CategoryPageSection[]
    section: CategoryPageSection
    pageDataFromBFF: BFFPageData
    merchandisingCategoryId: string
    type: string
    key: number
    pageCategoryPageV3: pageCategoryPageV3
    locale: string
    sectionNumber: number
    nextSection?: CategoryPageSection
    index: number
    reviewsData?: ReviewsResponse
    enableCampaignCallouts?: boolean
    sidebarEnabled: boolean
    calloutsLocalization: ExtendedMtsLocalizationResponse
  },
  templateConfiguration: TemplateConfiguration,
  experimentData: ExperimentData,
  automatedExperience?: AutomatedExperience,
  featureFlags?: FeatureFlags,
  productRecommendations?: ProductRecommendationsData,
  adaptedNewArrivalsResponse?: AdaptedResponse | null,
  adaptedBestsellersResponse?: AdaptedResponse | null,
  pageLevel?: PageLevel | null
) => {
  const {
    sections,
    section,
    pageDataFromBFF,
    type,
    key,
    pageCategoryPageV3,
    locale,
    sectionNumber,
    nextSection,
    index,
    reviewsData,
    enableCampaignCallouts,
    sidebarEnabled,
    calloutsLocalization
  } = sectionProps

  const UniqueTestSection = TestSection(experimentData, type)
  if (UniqueTestSection) {
    return <UniqueTestSection {...sectionProps} />
  }

  switch (type) {
    case 'anchor links': {
      return <AnchorBarWrapper pageCategoryPageV3={pageCategoryPageV3} sections={sections} />
    }
    case 'promo banner':
      return <PromoBanner key={key} configuration={section} sectionNumber={sectionNumber} />
    case 'section header':
      return (
        <PageHeaderContainer
          configuration={section as PageHeaderType | SideBySideHeroType}
          pageDataFromBFF={pageDataFromBFF}
        />
      )
    case 'side by side hero':
      return (
        showSideBySideHero(section) && (
          <BoundedContent paddingX={0} marginBottom={{ xs: 7, sm: 7, md: 8 }}>
            <SideBySideHero section={section as SideBySideHeroType} />
          </BoundedContent>
        )
      )
    case 'product grid':
    case 'product grid horizontal XL':
    case 'product grid horizontal':
      return (
        <ProductGrid
          key={key}
          section={section as ProductGridType}
          pageDataFromBFF={pageDataFromBFF}
          sectionNumber={sectionNumber}
        />
      )
    case 'product lane': {
      const productLaneProps = ProductTileCollectionUtil.createProductLaneModel(
        section as ProductLaneType,
        sectionNumber,
        nextSection,
        reviewsData,
        enableCampaignCallouts,
        pageDataFromBFF,
        automatedExperience,
        featureFlags
      )
      return (
        <ProductLane
          key={key}
          sectionData={productLaneProps}
          rawSectionData={section as ProductLaneType}
        />
      )
    }
    case 'information lane':
      return (
        <InformationLane
          key={key}
          section={section as InformationLaneType}
          sectionNumber={sectionNumber}
        />
      )
    case 'good better best':
      return (
        <GoodBetterBest
          section={section as GoodBetterBestSection}
          pageDataFromBFF={pageDataFromBFF}
        />
      )
    case 'information media section':
      return (
        showInformationMediaSection(section as InformationMediaType) && (
          <InformationMedia
            section={section as InformationMediaType}
            sectionNumber={sectionNumber}
          />
        )
      )
    case 'virtual section':
      return (
        <VirtualSection
          data={(section as VirtualSectionType).value}
          mpvs={pageDataFromBFF?.mpvs || []}
          designServices={pageDataFromBFF?.designServices}
          uploadFlowMpv={pageDataFromBFF?.uploadFlowMpv}
          galleries={pageDataFromBFF?.galleries}
        />
      )
    case 'template lane':
      return (
        showTemplateLane(section, locale, pageDataFromBFF?.galleries) && (
          <TemplateLane
            key={key}
            content={section as TemplateLaneType}
            galleries={pageDataFromBFF?.galleries}
            nextSectionId={nextSection?.sectionId}
            nextSectionTitle={getSectionTitle(nextSection)}
            sectionNumber={sectionNumber}
          />
        )
      )
    case 'template collection lane':
      return (
        <CategoryRowWrapper
          configuration={section as TemplateCollectionType}
          galleries={pageDataFromBFF?.galleries}
          nextSectionId={nextSection?.sectionId}
          nextSectionTitle={getSectionTitle(nextSection)}
          sectionNumber={sectionNumber}
        />
      )
    case 'template collection grid': {
      const nextSectionTitle = getSectionTitle(nextSection)
      const templateCollectionGridProps = {
        configuration: section as TemplateCollectionType,
        ...(pageDataFromBFF?.galleries && { galleries: pageDataFromBFF.galleries }),
        ...(nextSection?.sectionId && { nextSectionId: nextSection.sectionId }),
        ...(nextSectionTitle && { nextSectionTitle }),
        index
      }

      return (
        <CategoryGridWrapper
          {...(templateCollectionGridProps satisfies React.ComponentProps<
            typeof CategoryGridWrapper
          >)}
        />
      )
    }
    case 'seo':
      return (
        showSeoSection(pageCategoryPageV3.seoSectionTitle, pageCategoryPageV3.seoHTML) && (
          <Seo
            key={key}
            sectionTitle={pageCategoryPageV3.seoSectionTitle}
            content={pageCategoryPageV3.seoHTML}
          />
        )
      )
    case 'social module':
      return (
        showOlapic(
          pageCategoryPageV3.socialCurations,
          pageCategoryPageV3.socialCurationsTitle,
          pageCategoryPageV3.socialCurationsSubtitle
        ) && (
          <SocialCurations
            key={key}
            socialCurationsTitle={pageCategoryPageV3.socialCurationsTitle}
            socialCurationsSubtitle={pageCategoryPageV3.socialCurationsSubtitle}
            data={pageCategoryPageV3.socialCurations}
          />
        )
      )
    case 'stock options': {
      const automatedStockOptionData = buildStockOptionModel(
        section as StockOptionsType,
        automatedExperience,
        featureFlags
      )
      if (automatedStockOptionData) {
        return <AutomatedStockOptions stockOptionData={automatedStockOptionData} />
      }
      return null
    }
    case 'design template':
      return <DesignTemplate />

    case 'frequently ordered':
    case 'related products':
      return <Recommendation type={type} />
    case 'bestsellers': {
      return (
        <Bestsellers
          section={section as BestsellersType}
          sectionNumber={sectionNumber}
          productRecommendations={productRecommendations?.bestsellers ?? null}
          adaptedResponse={adaptedBestsellersResponse ?? undefined}
          pageLevel={pageLevel ?? null}
          sidebarEnabled={sidebarEnabled}
          calloutsLocalization={calloutsLocalization}
        />
      )
    }
    case 'new arrivals':
      return (
        <NewArrivals
          section={section as NewArrivalsType}
          sectionNumber={sectionNumber}
          productRecommendations={productRecommendations?.newArrivals ?? null}
          adaptedResponse={adaptedNewArrivalsResponse}
          pageLevel={pageLevel ?? null}
          sidebarEnabled={sidebarEnabled}
          calloutsLocalization={calloutsLocalization}
        />
      )
    default:
      return null
  }
}

export default PageSections
